import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV_LINKS } from "@/data/content";
import { ThemeToggle } from "@/components/theme";
import { Cta } from "@/components/Cta";
import { Logo } from "@/components/Logo";
import { cn } from "@/lib/utils";

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 20);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => setOpen(false), [location.pathname]);

    return (
        <header className="fixed inset-x-0 top-0 z-50">
            <div
                className={cn(
                    "border-b transition-all duration-500 ease-premium",
                    scrolled
                        ? "border-border bg-background/70 backdrop-blur-xl"
                        : "border-transparent bg-transparent",
                )}
            >
                <nav className="mx-auto flex h-16 max-w-7xl items-center justify-between px-6 lg:px-10">
                    <Logo />

                    <div className="hidden items-center gap-1 md:flex">
                        {NAV_LINKS.map((l) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                data-testid={`nav-link-${l.to.slice(1)}`}
                                className={({ isActive }) =>
                                    cn(
                                        "relative rounded-full px-4 py-2 text-sm font-medium transition-colors duration-300 ease-premium hover:text-foreground",
                                        isActive
                                            ? "text-foreground"
                                            : "text-muted-foreground",
                                    )
                                }
                            >
                                {({ isActive }) => (
                                    <>
                                        {l.label}
                                        {isActive && (
                                            <motion.span
                                                layoutId="nav-active"
                                                className="absolute inset-0 -z-10 rounded-full bg-secondary"
                                                transition={{
                                                    duration: 0.4,
                                                    ease: [0.16, 1, 0.3, 1],
                                                }}
                                            />
                                        )}
                                    </>
                                )}
                            </NavLink>
                        ))}
                    </div>

                    <div className="flex items-center gap-2">
                        <ThemeToggle />
                        <div className="hidden md:block">
                            <Cta
                                to="/contact"
                                size="md"
                                testId="nav-contact-cta"
                                withArrow={false}
                            >
                                Book Assessment
                            </Cta>
                        </div>
                        <button
                            data-testid="nav-mobile-toggle"
                            aria-label="Open menu"
                            onClick={() => setOpen((v) => !v)}
                            className="grid h-9 w-9 place-items-center rounded-full border border-border md:hidden"
                        >
                            {open ? (
                                <X className="h-5 w-5" strokeWidth={1.5} />
                            ) : (
                                <Menu className="h-5 w-5" strokeWidth={1.5} />
                            )}
                        </button>
                    </div>
                </nav>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                        className="overflow-hidden border-b border-border bg-background/95 backdrop-blur-xl md:hidden"
                    >
                        <div className="flex flex-col gap-1 px-6 py-5">
                            {NAV_LINKS.map((l) => (
                                <NavLink
                                    key={l.to}
                                    to={l.to}
                                    data-testid={`nav-mobile-link-${l.to.slice(1)}`}
                                    className="rounded-lg px-3 py-3 text-lg font-medium text-foreground hover:bg-secondary"
                                >
                                    {l.label}
                                </NavLink>
                            ))}
                            <Cta
                                to="/contact"
                                size="lg"
                                className="mt-3 w-full"
                                testId="nav-mobile-contact-cta"
                                withArrow={false}
                            >
                                Book Assessment
                            </Cta>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
}
